import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, getContent, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StrategiesGrid } from "@/components/StrategyCard";
import { TradingTerminal, DexTerminal, FuturesTerminal } from "@/components/qcore";
import { StatusPill } from "@/components/primitives";
import { VirtualCard3D, BonusCards, RankPanel, OrgTree, ReferralNetwork, RankLadder } from "./viz";
import { fmtCents, fmtDate } from "./util";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import {
  Circuitry, Broadcast, Check, Copy, CreditCard, LockKey, ShieldCheck, UserCircle,
  ArrowClockwise, Trophy, BookOpen, ArrowLineDown, WarningCircle, Eye, EyeSlash, UsersThree,
  IdentificationCard, Planet, FilePdf, FilePpt, VideoCamera, GraduationCap, Megaphone, Question, ArrowUpRight,
  TrendUp, TrendDown, CalendarBlank, PlayCircle, Article, Sparkle,
} from "@phosphor-icons/react";

const card = "rounded-xl border border-white/10 bg-[#0A0B10] p-6";
const inputCls = "w-full rounded-lg border border-white/10 bg-[#08090F] px-3.5 py-2.5 text-sm text-white focus:border-[#2DD4BF]/50 focus:outline-none";

function DemoConnPending({ label }) {
  return (
    <div className="rounded-xl border border-dashed border-amber-400/30 bg-amber-400/[0.04] p-5 flex items-center gap-3">
      <Broadcast size={20} className="text-amber-400" />
      <div>
        <p className="text-sm font-semibold text-amber-200">LIVE DATA CONNECTION PENDING</p>
        <p className="text-xs text-[#94A3B8]">{label} — showing clearly-labeled DEMO DATA until Q-Core APIs are connected.</p>
      </div>
    </div>
  );
}

const DEMO_TRADES = [
  ["Binance", "BTC/USDT", "0.14%", "+$42.10"],
  ["Kraken", "ETH/USDT", "0.09%", "+$18.30"],
  ["OKX", "SOL/USDT", "0.21%", "+$27.75"],
  ["Bybit", "ETH/BTC", "0.07%", "+$9.40"],
];

export function QCoreSection() {
  const [sub, setSub] = useState("overview");
  const tabs = [["overview", "Overview"], ["cex", "CEX Live"], ["dex", "DEX Live"], ["futures", "Futures Live"], ["signals", "Quantum Signals"]];
  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-white/10 overflow-x-auto">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`qcore-subtab-${k}`} className={`px-4 py-3 text-sm whitespace-nowrap border-b-2 -mb-px ${sub === k ? "border-[#2DD4BF] text-white" : "border-transparent text-[#94A3B8] hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {sub === "overview" && (
        <div className="space-y-6">
          <div className={card}>
            <h3 className="font-bold text-white mb-1">Q-Core Engine</h3>
            <p className="text-sm text-[#94A3B8]">Six parallel arbitrage strategies analyzed simultaneously. Quantum-inspired optimization on classical infrastructure.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              {["Q-Core Engine ONLINE", "Market Data CONNECTED", "Execution CONNECTED", "Blockchain ACTIVE"].map((s) => (
                <span key={s} className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs text-emerald-300 font-mono"><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />{s}</span>
              ))}
            </div>
          </div>
          <StrategiesGrid />
        </div>
      )}
      {sub === "cex" && (
        <div className="space-y-4"><TradingTerminal market="cex" /></div>
      )}
      {sub === "dex" && (
        <div className="space-y-4"><DexTerminal /></div>
      )}
      {sub === "futures" && (
        <div className="space-y-4"><FuturesTerminal /></div>
      )}
      {sub === "signals" && (
        <div className="space-y-4">
          <DemoConnPending label="Quantum Signals" />
          <div className="grid md:grid-cols-2 gap-4">
            {[["Cross-Exchange", "BTC/USDT", "ACTIVE"], ["Statistical", "ETH/USDT", "COMPLETED"]].map((s, i) => (
              <div key={i} className={card}>
                <div className="flex items-center justify-between mb-2"><span className="text-xs font-mono text-[#0E9E90]">{s[0]}</span><StatusPill status={s[2] === "ACTIVE" ? "ACTIVE" : "monitoring"} /></div>
                <p className="text-white font-bold">{s[1]}</p>
                <p className="text-xs text-[#64748B] mt-2">Informational technology output — not financial advice.</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MembershipSection() {
  const [lic, setLic] = useState(null);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [receipts, setReceipts] = useState([]);
  useEffect(() => {
    api.get("/license/me").then((r) => setLic(r.data)).catch(() => {});
    api.get("/receipts").then((r) => setReceipts(r.data.receipts || [])).catch(() => {});
    getContent("plans").then(setPlans).catch(() => {});
  }, []);
  const buy = async (p) => {
    if (!p.lookup_key) { window.location.href = "/contact"; return; }
    setBusy(p.lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: p.lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error("Could not start checkout"); setBusy(null); }
  };
  const active = lic?.active_license;
  return (
    <div className="space-y-6">
      <div className={`${card} ${active ? "border-[#2DD4BF]/25 bg-[#2DD4BF]/[0.04]" : ""}`}>
        <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-white">Membership Status</h3><StatusPill status={lic?.state === "expired" ? "monitoring" : active ? "ACTIVE" : "monitoring"} /></div>
        {active ? (
          <>
            <p className="text-2xl font-black text-white">{active.name}</p>
            <p className="text-sm text-[#94A3B8] mt-1">Expires {fmtDate(active.expires_at)} · {active.days_remaining} days remaining</p>
            {lic?.reminders?.[0] && <p className="mt-3 text-sm text-amber-300">{lic.reminders[0].message}</p>}
          </>
        ) : <p className="text-[#94A3B8]">No active membership. A membership license unlocks Q-Core platform access.</p>}
      </div>
      <div>
        <h3 className="font-bold text-white mb-4">Plans</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {plans.map((p) => (
            <div key={p.id} className={`${card} ${p.highlight ? "border-[#2DD4BF]/40" : ""} flex flex-col`}>
              <h4 className="font-bold text-white">{p.name}</h4>
              <div className="mt-2 flex items-baseline gap-1"><span className="text-2xl font-black text-white">{p.price}</span><span className="text-sm text-[#64748B]">{p.period}</span></div>
              <ul className="mt-4 space-y-2 flex-1">{(p.features || []).slice(0, 4).map((f) => <li key={f} className="flex items-start gap-2 text-xs text-[#cbd5e1]"><Check size={14} className="text-[#2DD4BF] mt-0.5 shrink-0" />{f}</li>)}</ul>
              <button onClick={() => buy(p)} disabled={busy === p.lookup_key} className="mt-4 w-full px-4 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white disabled:opacity-60">{busy === p.lookup_key ? "Starting…" : (active ? "Renew / Upgrade" : p.cta)}</button>
            </div>
          ))}
        </div>
      </div>
      <div className={card} data-testid="receipts-panel">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-white">Receipts</h3>
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#64748B]">{receipts.length} record{receipts.length === 1 ? "" : "s"}</span>
        </div>
        {receipts.length ? (
          <div className="divide-y divide-white/5">
            {receipts.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 py-3" data-testid={`receipt-${r.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`grid place-items-center h-9 w-9 rounded-lg shrink-0 ${r.type === "license" ? "bg-[#2DD4BF]/10 text-[#2DD4BF]" : "bg-[#00E676]/10 text-[#00E676]"}`}>
                    {r.type === "license" ? <IdentificationCard size={17} /> : <Planet size={17} />}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm text-white truncate">{r.title}</p>
                    <p className="text-[11px] text-[#64748B] font-mono">{r.id} · {fmtDate(r.date)} · {r.type === "license" ? "License" : "Portfolio"}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono text-sm text-white">{fmtCents(r.amount_cents)}</p>
                  <span className={`font-mono text-[9px] uppercase tracking-widest ${r.status === "paid" || r.status === "active" || r.status === "completed" ? "text-[#00E676]" : "text-amber-300"}`}>{r.status}</span>
                </div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-[#64748B]">No receipts yet. License and portfolio purchases will appear here.</p>}
      </div>

      <p className="text-xs text-[#64748B] font-mono">License fees provide access to software and services and do not represent an investment or guaranteed return.</p>
    </div>
  );
}

export function VirtualCardSection() {
  const { user } = useAuth();
  const [state, setState] = useState({ joined: false });
  const [country, setCountry] = useState("");
  useEffect(() => { api.get("/virtual-card/waitlist/me").then((r) => setState(r.data)).catch(() => {}); }, []);
  const join = async () => {
    try { await api.post("/virtual-card/waitlist", { country }); setState({ joined: true }); toast.success("You're on the waiting list."); }
    catch { toast.error("Could not join waitlist"); }
  };
  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-white/5 bg-gradient-to-br from-[#12131A] to-[#08090F] p-8 relative overflow-hidden qs-aurora">
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-mono text-amber-300 mb-4"><CreditCard size={13} /> COMING SOON</span>
            <h2 className="text-3xl font-black text-white max-w-lg">Quantum Virtual Card</h2>
            <p className="text-[#94A3B8] mt-3 max-w-lg">Spend directly from your wallet balances. A secure, privacy-conscious virtual card is under development. Move your cursor over the card to explore.</p>
          </div>
          <div className="flex justify-center lg:justify-end">
            <VirtualCard3D holder={user?.name || "Member"} />
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[["Core Features", "Spend from Main/Staking/Referral balances"], ["Security", "Tokenized, with controls and freeze"], ["Rollout", "Phased regional launch after review"]].map(([t, d]) => (
          <div key={t} className={card}><h4 className="font-bold text-white mb-1.5">{t}</h4><p className="text-sm text-[#94A3B8]">{d}</p></div>
        ))}
      </div>
      <div className={card}>
        {state.joined ? (
          <div className="flex items-center gap-3"><Check size={22} className="text-emerald-400" /><p className="text-sm text-white">You're on the waiting list. We'll notify you at launch.</p></div>
        ) : (
          <div className="flex flex-col sm:flex-row gap-3">
            <input className={`${inputCls} flex-1`} placeholder="Country" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="vc-country" />
            <button onClick={join} data-testid="vc-join" className="px-6 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white">Join Waiting List</button>
          </div>
        )}
      </div>
    </div>
  );
}

const WD_STATE_COLORS = {
  KYC_PENDING: "text-amber-300 border-amber-400/40 bg-amber-400/10",
  UNDER_REVIEW: "text-[#2DD4BF] border-[#2DD4BF]/40 bg-[#2DD4BF]/10",
  APPROVED: "text-[#2DD4BF] border-[#2DD4BF]/40 bg-[#2DD4BF]/10",
  PROCESSING: "text-[#0E9E90] border-[#0E9E90]/40 bg-[#0E9E90]/10",
  COMPLETED: "text-emerald-300 border-emerald-400/40 bg-emerald-400/10",
  REJECTED: "text-red-300 border-red-400/40 bg-red-400/10",
  CANCELLED: "text-[#94A3B8] border-white/15 bg-white/5",
  FAILED: "text-red-300 border-red-400/40 bg-red-400/10",
};

export function WithdrawalsSection() {
  const [sub, setSub] = useState("initiate");
  const [wallets, setWallets] = useState({});
  const [kyc, setKyc] = useState(null);
  const [history, setHistory] = useState([]);
  const [f, setF] = useState({ amount: "", wallet: "main", asset: "USDT", network: "Ethereum (ERC-20)", address: "", memo: "" });
  const [busy, setBusy] = useState(false);
  const [kycDoc, setKycDoc] = useState("Passport");

  const load = () => {
    api.get("/wallets").then((r) => setWallets(r.data.wallets)).catch(() => {});
    api.get("/kyc/me").then((r) => setKyc(r.data)).catch(() => {});
    api.get("/withdrawals").then((r) => setHistory(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/withdrawals", { ...f, amount: parseFloat(f.amount) });
      toast.success("Withdrawal requested.");
      setF({ ...f, amount: "", address: "" });
      load(); setSub("history");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const submitKyc = async () => {
    try { await api.post("/kyc/submit", { doc_type: kycDoc, reference: "uploaded" }); toast.success("KYC submitted for review."); load(); }
    catch { toast.error("KYC submission failed"); }
  };
  const cancel = async (wid) => {
    try { await api.post(`/withdrawals/${wid}/cancel`, {}); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const verified = kyc?.status === "verified";
  return (
    <div className="space-y-6">
      <div className="flex gap-2 border-b border-white/10">
        {[["initiate", "Initiate Withdrawal"], ["history", "Withdrawal History"]].map(([k, l]) => (
          <button key={k} onClick={() => setSub(k)} data-testid={`wd-tab-${k}`} className={`px-4 py-3 text-sm border-b-2 -mb-px ${sub === k ? "border-[#2DD4BF] text-white" : "border-transparent text-[#94A3B8] hover:text-white"}`}>{l}</button>
        ))}
      </div>

      {sub === "initiate" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <form onSubmit={submit} className={`${card} lg:col-span-2`} data-testid="withdraw-form">
            <h3 className="font-bold text-white mb-4">Initiate Withdrawal</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="block text-sm text-[#cbd5e1] mb-1.5">Amount (USD)</label><input data-testid="wd-amount" type="number" className={inputCls} value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Min $10.00" /></div>
              <div><label className="block text-sm text-[#cbd5e1] mb-1.5">From Wallet</label>
                <select data-testid="wd-wallet" className={inputCls} value={f.wallet} onChange={(e) => setF({ ...f, wallet: e.target.value })}>
                  <option value="main" className="bg-[#08090F]">Main — {fmtCents(wallets.main?.available_cents)}</option>
                  <option value="staking" className="bg-[#08090F]">Staking — {fmtCents(wallets.staking?.available_cents)}</option>
                  <option value="referral" className="bg-[#08090F]">Referral — {fmtCents(wallets.referral?.available_cents)}</option>
                </select>
              </div>
              <div><label className="block text-sm text-[#cbd5e1] mb-1.5">Stablecoin</label><select data-testid="wd-asset" className={inputCls} value={f.asset} onChange={(e) => setF({ ...f, asset: e.target.value })}><option className="bg-[#08090F]">USDT</option><option className="bg-[#08090F]">USDC</option></select></div>
              <div><label className="block text-sm text-[#cbd5e1] mb-1.5">Network</label><select className={inputCls} value={f.network} onChange={(e) => setF({ ...f, network: e.target.value })}><option className="bg-[#08090F]">Ethereum (ERC-20)</option><option className="bg-[#08090F]">Tron (TRC-20)</option></select></div>
              <div className="sm:col-span-2"><label className="block text-sm text-[#cbd5e1] mb-1.5">Destination Address</label><input data-testid="wd-address" className={inputCls} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="0x… / T…" /></div>
            </div>
            <p className="text-xs text-[#64748B] mt-3 font-mono">Flat network fee: $1.00 · Funds are held on request and refunded automatically if rejected or cancelled.</p>
            <button type="submit" disabled={busy} data-testid="wd-submit" className="mt-4 px-6 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white disabled:opacity-60">{busy ? "Submitting…" : "Request Withdrawal"}</button>
          </form>
          <div className={card}>
            <div className="flex items-center gap-2 mb-3"><ShieldCheck size={18} className="text-[#2DD4BF]" /><h3 className="font-bold text-white">Identity Verification</h3></div>
            {verified ? (
              <div className="flex items-center gap-2 text-sm text-emerald-300"><Check size={16} /> KYC verified</div>
            ) : (
              <>
                <p className="text-sm text-[#94A3B8] mb-3">Your first withdrawal requires identity verification (KYC status: <span className="font-mono text-amber-300">{kyc?.status || "unverified"}</span>).</p>
                {kyc?.status !== "pending" && (
                  <>
                    <select className={`${inputCls} mb-3`} value={kycDoc} onChange={(e) => setKycDoc(e.target.value)} data-testid="kyc-doc">
                      <option className="bg-[#08090F]">Passport</option><option className="bg-[#08090F]">Government ID</option><option className="bg-[#08090F]">Driver's License</option>
                    </select>
                    <button onClick={submitKyc} data-testid="kyc-submit" className="w-full px-4 py-2.5 rounded-full border border-white/15 text-white text-sm hover:bg-white/5">Submit for Verification</button>
                  </>
                )}
                {kyc?.status === "pending" && <p className="text-xs text-amber-300 font-mono">Under review by our team.</p>}
              </>
            )}
          </div>
        </div>
      )}

      {sub === "history" && (
        <div className={`${card} overflow-x-auto`} data-testid="wd-history">
          <table className="w-full text-sm">
            <thead><tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[#64748B] border-b border-white/10">
              <th className="py-2">ID</th><th className="py-2">Amount</th><th className="py-2">Net</th><th className="py-2">Asset</th><th className="py-2">Status</th><th className="py-2">Tx</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {history.length ? history.map((w) => (
                <tr key={w.id} className="border-b border-white/5">
                  <td className="py-2.5"><code className="font-mono text-xs text-[#2DD4BF]">{w.withdrawal_id}</code></td>
                  <td className="py-2.5 font-mono text-white">{fmtCents(w.amount_cents)}</td>
                  <td className="py-2.5 font-mono text-[#cbd5e1]">{fmtCents(w.net_cents)}</td>
                  <td className="py-2.5 text-[#94A3B8]">{w.asset} · {w.network?.split(" ")[0]}</td>
                  <td className="py-2.5"><span className={`rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase ${WD_STATE_COLORS[w.status] || ""}`}>{w.status.replace("_", " ")}</span></td>
                  <td className="py-2.5">{w.tx_hash ? <a href={`https://etherscan.io/tx/${w.tx_hash}`} target="_blank" rel="noopener noreferrer" className="text-[#2DD4BF] text-xs">View</a> : <span className="text-[#64748B] text-xs">—</span>}</td>
                  <td className="py-2.5">{["REQUESTED", "KYC_PENDING", "UNDER_REVIEW"].includes(w.status) && <button onClick={() => cancel(w.withdrawal_id)} className="text-red-400 text-xs hover:underline">Cancel</button>}</td>
                </tr>
              )) : <tr><td colSpan={7} className="py-6 text-center text-sm text-[#64748B] font-mono">No withdrawals yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function ReferralsSection() {
  const [data, setData] = useState(null);
  const [levels, setLevels] = useState([]);
  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/quantum-flex/me").then((r) => setLevels(r.data.levels || [])).catch(() => {});
  }, []);
  const code = data?.code || "…";
  const link = `${window.location.origin}/register?ref=${code}`;
  return (
    <div className="space-y-6">
      <div className={card}>
        <h3 className="font-bold text-white mb-3">Your Referral Link</h3>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-3 mb-2">
          <code className="font-mono text-lg text-[#2DD4BF]">{code}</code>
          <button onClick={() => { navigator.clipboard?.writeText(code); toast.success("Copied"); }} className="text-[#94A3B8] hover:text-[#2DD4BF]"><Copy size={16} /></button>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-lg border border-white/10 bg-black/30 px-4 py-3">
          <code className="font-mono text-xs text-[#94A3B8] truncate">{link}</code>
          <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Copied"); }} className="shrink-0 text-[#94A3B8] hover:text-[#2DD4BF]"><Copy size={16} /></button>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[["Total", data?.total ?? 0], ["Licensed", data?.active ?? 0], ["Levels", 7]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-white/10 bg-black/20 p-3 text-center"><p className="font-mono text-xl text-white">{v}</p><p className="text-[10px] uppercase font-mono text-[#64748B] mt-1">{l}</p></div>
          ))}
        </div>
      </div>
      <div className={card}>
        <h3 className="font-bold text-white mb-1">Seven-Level Network Map</h3>
        <p className="text-xs text-[#64748B] mb-5">A live visualization of your organization across all seven levels.</p>
        <ReferralNetwork levels={levels} />
      </div>
    </div>
  );
}

function QuantumPoints() {
  const [data, setData] = useState(null);
  const load = () => api.get("/points/me").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const redeem = async (key) => {
    try { await api.post("/points/redeem", { reward_key: key }); toast.success("Redeemed!"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className={card} data-testid="points-panel">
      <div className="flex items-center gap-2 mb-4"><Trophy size={20} className="text-[#2DD4BF]" /><h3 className="font-bold text-white">Quantum Points</h3></div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[["Available", data?.available ?? 0], ["Earned", data?.earned ?? 0], ["Redeemed", data?.redeemed ?? 0]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-white/10 bg-black/20 p-4 text-center"><p className="font-mono text-2xl text-white">{v}</p><p className="text-[10px] uppercase font-mono text-[#64748B] mt-1">{l}</p></div>
        ))}
      </div>
      <p className="text-sm font-semibold text-white mb-2">Rewards</p>
      <div className="grid sm:grid-cols-3 gap-3">
        {(data?.rewards || []).map((rw) => (
          <div key={rw.key} className="rounded-lg border border-white/10 bg-black/20 p-4">
            <p className="text-sm font-semibold text-white">{rw.name}</p>
            <p className="text-xs text-[#94A3B8] mt-0.5 mb-3">{rw.desc}</p>
            <button onClick={() => redeem(rw.key)} disabled={(data?.available ?? 0) < rw.cost} data-testid={`redeem-${rw.key}`} className="w-full px-3 py-2 rounded-full bg-[#2DD4BF] text-[#030305] text-xs font-semibold hover:bg-white disabled:opacity-40">{rw.cost.toLocaleString()} pts</button>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-[#64748B] font-mono mt-4">Earn points automatically from team production. Redemptions are fulfilled by the company per program terms.</p>
    </div>
  );
}

export function QuantumFlexSection() {
  const [data, setData] = useState(null);
  const [ranks, setRanks] = useState([]);
  const [openRank, setOpenRank] = useState(null);
  useEffect(() => {
    api.get("/quantum-flex/me").then((r) => { setData(r.data); setOpenRank(r.data.current_rank?.level || 1); }).catch(() => {});
    api.get("/quantum-flex/ranks").then((r) => setRanks(r.data)).catch(() => {});
  }, []);
  const cur = data?.current_rank;
  const nxt = data?.next_rank;
  return (
    <div className="space-y-6">
      <div className={card}>
        <div className="flex items-center gap-3 mb-4"><Trophy size={22} className="text-[#2DD4BF]" weight="duotone" /><h3 className="font-bold text-white">Quantum Flex — Leadership</h3></div>
        <div className="grid sm:grid-cols-4 gap-3">
          {[["Current Rank", cur?.name || "—"], ["Team Sales Volume", fmtCents(data?.team_volume_cents)], ["Commissions Earned", fmtCents(data?.commissions_cents)], ["Rank Bonuses", fmtCents(data?.rank_bonuses_cents)]].map(([l, v]) => (
            <div key={l} className="rounded-lg border border-white/10 bg-black/20 p-4"><p className="text-[10px] uppercase font-mono text-[#64748B]">{l}</p><p className="text-white mt-1 font-semibold">{v}</p></div>
          ))}
        </div>
        {nxt && (
          <div className="mt-5" data-testid="flex-progress">
            <div className="flex justify-between text-xs text-[#94A3B8] mb-1.5"><span>Progress to {nxt.name}</span><span className="font-mono">{data?.progress}%</span></div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#0E9E90] to-[#2DD4BF]" style={{ width: `${data?.progress || 0}%` }} /></div>
            <p className="text-[10px] text-[#64748B] font-mono mt-2">Requires team sales {fmtCents(nxt.team_req)} · personal sales {fmtCents(nxt.personal_req ?? nxt.first_req)}</p>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-[#2DD4BF]/20 bg-[#2DD4BF]/[0.04] p-4" data-testid="flex-sales-note">
        <p className="text-sm text-white font-semibold mb-1">Driven by product &amp; license sales</p>
        <p className="text-xs text-[#94A3B8] leading-relaxed">Rank advancement and 7-level commissions are calculated from real <span className="text-[#2DD4BF]">sales volume</span> — Q-Core licenses, Quantum Signals and VIP memberships purchased by you and your team — not from portfolio deposits.</p>
      </div>

      <div className={card}>
        <div className="flex items-center gap-3 mb-1"><Trophy size={20} className="text-[#2DD4BF]" weight="duotone" /><h3 className="font-bold text-white">Rank Ladder</h3></div>
        <p className="text-xs text-[#64748B] mb-4">Your live position across all 12 Quantum Flex ranks — and the exact sales left to level up.</p>
        <RankLadder ranks={ranks} current={cur} next={nxt} teamVol={data?.team_volume_cents || 0} personalVol={data?.personal_volume_cents || 0} progress={data?.progress || 0} />
      </div>

      <div>
        <h3 className="font-semibold text-white mb-3">Leadership Bonuses</h3>
        <BonusCards />
      </div>

      <QuantumPoints />
      <div className={card}>
        <h3 className="font-bold text-white mb-1">Seven-Level Organization</h3>
        <p className="text-xs text-[#64748B] mb-4">Click any level to expand its production and member breakdown.</p>
        <OrgTree levels={data?.levels || []} />
      </div>

      <div className={card}>
        <h3 className="font-bold text-white mb-1">12 Ranks</h3>
        <p className="text-xs text-[#64748B] mb-4">Expand any rank for qualification requirements, rewards and benefits.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {ranks.map((rk) => {
            const state = cur ? (rk.level < cur.level ? "achieved" : rk.level === cur.level ? "current" : "locked") : "locked";
            return (
              <RankPanel
                key={rk.level} rk={rk} state={state} progress={data?.progress || 0}
                open={openRank === rk.level} onToggle={() => setOpenRank(openRank === rk.level ? null : rk.level)}
              />
            );
          })}
        </div>
        <p className="text-xs text-[#64748B] font-mono mt-4">Rank qualification is calculated from verifiable product &amp; license sales volume. Commissions (7 levels) are credited to your Referral wallet when your team purchases licenses, Signals or VIP.</p>
      </div>
    </div>
  );
}

const RES_THUMB = {
  Presentations: { icon: FilePpt, color: "#F59E0B" },
  PDFs: { icon: FilePdf, color: "#FF2E63" },
  Training: { icon: GraduationCap, color: "#2DD4BF" },
  Videos: { icon: VideoCamera, color: "#14B8A6" },
  "Marketing Materials": { icon: Megaphone, color: "#00E676" },
  Marketing: { icon: Megaphone, color: "#00E676" },
  "Product Education": { icon: BookOpen, color: "#2DD4BF" },
  Education: { icon: BookOpen, color: "#2DD4BF" },
  Compliance: { icon: ShieldCheck, color: "#94A3B8" },
  FAQs: { icon: Question, color: "#2DD4BF" },
};
const resThumb = (c) => RES_THUMB[c] || { icon: BookOpen, color: "#2DD4BF" };

export function ResourcesSection() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  useEffect(() => { api.get("/resources").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const cats = ["all", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))];
  const countFor = (c) => (c === "all" ? items.length : items.filter((i) => i.category === c).length);
  const filtered = items.filter((i) =>
    (cat === "all" || i.category === cat) &&
    (!q || i.title?.toLowerCase().includes(q.toLowerCase()) || i.desc?.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="space-y-5">
      <input className={`${inputCls} w-full`} placeholder="Search the resource library…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="res-search" />
      <div className="flex flex-wrap gap-2" data-testid="res-chips">
        {cats.map((c) => {
          const on = cat === c;
          const t = c === "all" ? null : resThumb(c);
          return (
            <button key={c} onClick={() => setCat(c)} data-testid={`res-chip-${c}`}
              className={`inline-flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-xs transition-colors ${on ? "border-[#0E9E90]/50 bg-[#0E9E90]/[0.08] text-white" : "border-white/10 bg-white/[0.02] text-[#94A3B8] hover:text-white hover:border-white/20"}`}>
              {t && <span className="h-2 w-2 rounded-sm" style={{ background: t.color }} />}
              {c === "all" ? "All" : c}
              <span className="font-mono text-[10px] text-[#64748B]">{countFor(c)}</span>
            </button>
          );
        })}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="res-list">
        {filtered.length ? filtered.map((it) => {
          const t = resThumb(it.category);
          const TIcon = t.icon;
          return (
            <a key={it.id} href={it.url} target="_blank" rel="noopener noreferrer" data-testid={`res-item-${it.id}`}
              className="qs-beam group rounded-xl border border-white/5 bg-[#0A0B10] overflow-hidden hover:border-white/10 transition-colors flex flex-col">
              <div className="relative aspect-[16/9] overflow-hidden">
                {it.thumbnail ? (
                  <img src={it.thumbnail} alt={it.title} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="h-full w-full grid place-items-center" style={{ background: `radial-gradient(120% 120% at 30% 20%, ${t.color}22, #0A0B10 70%)` }}>
                    <div className="absolute inset-0 qs-grid opacity-[0.15]" />
                    <TIcon size={40} weight="duotone" style={{ color: t.color }} className="relative group-hover:scale-110 transition-transform" />
                  </div>
                )}
                <span className="absolute top-2.5 left-2.5 font-mono text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: `${t.color}1f`, color: t.color }}>{it.type || it.category}</span>
              </div>
              <div className="p-4 flex-1 flex flex-col">
                {it.category && <span className="font-mono text-[10px] uppercase tracking-widest text-[#64748B] mb-1">{it.category}</span>}
                <p className="font-semibold text-white leading-snug">{it.title}</p>
                <p className="text-xs text-[#94A3B8] mt-1.5 line-clamp-2 flex-1">{it.desc}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#2DD4BF] group-hover:text-white transition-colors">Open resource <ArrowUpRight size={13} /></span>
              </div>
            </a>
          );
        }) : <p className="text-sm text-[#64748B] font-mono col-span-full">No resources found.</p>}
      </div>
    </div>
  );
}

export function SecuritySection() {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (f.new_password !== f.confirm) { toast.error("Passwords do not match"); return; }
    if (f.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password });
      toast.success("Password updated");
      setF({ current_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <form onSubmit={submit} className={card} data-testid="security-password-form">
        <div className="flex items-center gap-2 mb-4"><LockKey size={20} className="text-[#2DD4BF]" /><h3 className="font-bold text-white">Update Password</h3></div>
        <div className="space-y-3">
          <input type={show ? "text" : "password"} className={inputCls} placeholder="Current password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="cur-pass" />
          <input type={show ? "text" : "password"} className={inputCls} placeholder="New password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-pass" />
          <input type={show ? "text" : "password"} className={inputCls} placeholder="Confirm new password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="confirm-pass" />
          <label className="flex items-center gap-2 text-xs text-[#94A3B8]"><input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="accent-[#2DD4BF]" /> Show passwords</label>
        </div>
        <button type="submit" disabled={busy} data-testid="change-pass-btn" className="mt-4 px-6 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white disabled:opacity-60">{busy ? "Updating…" : "Update Password"}</button>
      </form>
      <TwoFactorPanel />
    </div>
  );
}

function TwoFactorPanel() {
  const [st, setSt] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [codes, setCodes] = useState(null);
  const load = () => api.get("/2fa/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const start = async () => { try { const { data } = await api.post("/2fa/setup"); setSetup(data); } catch { toast.error("Failed"); } };
  const enable = async () => {
    try { const { data } = await api.post("/2fa/enable", { code }); setCodes(data.backup_codes); setSetup(null); setCode(""); load(); toast.success("2FA enabled"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code"); }
  };
  const disable = async () => {
    const c = window.prompt("Enter a current authenticator or backup code to disable 2FA:");
    if (!c) return;
    try { await api.post("/2fa/disable", { code: c }); setCodes(null); load(); toast.success("2FA disabled"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code"); }
  };
  const regen = async () => {
    const c = window.prompt("Enter a current code to regenerate backup codes:");
    if (!c) return;
    try { const { data } = await api.post("/2fa/recovery-codes", { code: c }); setCodes(data.backup_codes); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid code"); }
  };
  return (
    <div className={card} data-testid="twofa-panel">
      <div className="flex items-center gap-2 mb-4"><ShieldCheck size={20} className="text-[#2DD4BF]" /><h3 className="font-bold text-white">Two-Factor Authentication</h3></div>
      {st?.enabled ? (
        <>
          <div className="flex items-center gap-2 text-sm text-emerald-300 mb-3"><Check size={16} /> 2FA is enabled · {st.backup_codes_remaining} backup codes left</div>
          {codes && <BackupCodes codes={codes} />}
          <div className="flex gap-2 mt-3">
            <button onClick={regen} className="px-4 py-2 rounded-full border border-white/15 text-white text-sm hover:bg-white/5">Regenerate backup codes</button>
            <button onClick={disable} data-testid="twofa-disable" className="px-4 py-2 rounded-full border border-white/15 text-red-400 text-sm hover:bg-white/5">Disable 2FA</button>
          </div>
        </>
      ) : setup ? (
        <div className="space-y-3">
          <p className="text-sm text-[#94A3B8]">Scan with Google Authenticator / Authy, then enter the 6-digit code.</p>
          <div className="bg-white p-3 rounded-lg w-max"><QRCodeSVG value={setup.otpauth_uri} size={148} /></div>
          <p className="text-xs text-[#64748B] font-mono break-all">Secret: {setup.secret}</p>
          <input className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" data-testid="twofa-code" />
          <button onClick={enable} data-testid="twofa-enable" className="px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white">Verify & Enable</button>
        </div>
      ) : codes ? (
        <><p className="text-sm text-emerald-300 mb-3">2FA enabled — save these backup codes:</p><BackupCodes codes={codes} /></>
      ) : (
        <>
          <p className="text-sm text-[#94A3B8] mb-4">Add an authenticator app to secure your account and withdrawals.</p>
          <button onClick={start} data-testid="twofa-setup" className="px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white">Enable 2FA</button>
        </>
      )}
    </div>
  );
}

function BackupCodes({ codes }) {
  return (
    <div className="rounded-lg border border-amber-400/25 bg-amber-400/[0.05] p-3">
      <p className="text-xs text-amber-300 mb-2 font-mono">Store these one-time backup codes safely:</p>
      <div className="grid grid-cols-2 gap-1.5">{codes.map((c) => <code key={c} className="font-mono text-sm text-white">{c}</code>)}</div>
    </div>
  );
}

export function ProfileSection() {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ name: "", phone: "", country: "", address: "", postal_code: "", dob: "" });
  const [busy, setBusy] = useState(false);
  const loadedRef = React.useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    api.get("/auth/me").then((r) => {
      if (loadedRef.current) return;
      loadedRef.current = true;
      const u = r.data;
      setF({ name: u.name || "", phone: u.phone || "", country: u.country || "", address: u.address || "", postal_code: u.postal_code || "", dob: u.dob || "" });
    }).catch(() => {});
  }, []);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/profile", f); await refresh?.(); toast.success("Profile updated"); }
    catch { toast.error("Update failed"); } finally { setBusy(false); }
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className={`${card} lg:col-span-1 text-center`}>
        <span className="grid place-items-center h-20 w-20 rounded-full bg-[#2DD4BF]/15 text-[#2DD4BF] text-2xl font-black mx-auto">{user?.name?.[0]?.toUpperCase() || "M"}</span>
        <p className="font-bold text-white mt-4">{user?.name}</p>
        <p className="text-sm text-[#94A3B8]">{user?.email}</p>
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-mono text-amber-300"><ShieldCheck size={13} /> KYC: Not Verified</div>
      </div>
      <form onSubmit={save} className={`${card} lg:col-span-2`} data-testid="profile-form">
        <h3 className="font-bold text-white mb-4">Personal Information</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          {[["name", "Full Name"], ["phone", "Phone"], ["country", "Country"], ["postal_code", "Postal Code"], ["dob", "Date of Birth"]].map(([k, l]) => (
            <div key={k}><label className="block text-sm text-[#cbd5e1] mb-1.5">{l}</label><input className={inputCls} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`profile-${k}`} /></div>
          ))}
          <div className="sm:col-span-2"><label className="block text-sm text-[#cbd5e1] mb-1.5">Address</label><input className={inputCls} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} /></div>
        </div>
        <button type="submit" disabled={busy} data-testid="profile-save" className="mt-5 px-6 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white disabled:opacity-60">{busy ? "Saving…" : "Save Changes"}</button>
        <p className="text-xs text-[#64748B] font-mono mt-3">Verified KYC information cannot be edited without re-verification.</p>
        <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between gap-3">
          <div><p className="text-sm text-white font-semibold">Product Tour</p><p className="text-xs text-[#64748B]">Replay the guided walkthrough of your dashboard.</p></div>
          <button type="button" data-testid="profile-replay-tour" onClick={() => { localStorage.removeItem("qs_onboarded"); window.dispatchEvent(new Event("qs:start-tour")); }} className="shrink-0 px-4 py-2 rounded-full border border-white/15 text-white text-sm hover:bg-white/5">Replay Tour</button>
        </div>
      </form>
    </div>
  );
}

async function startCheckout(lookup_key, setBusy) {
  setBusy && setBusy(true);
  try {
    const { data } = await api.post("/payments/checkout", { lookup_key, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch { toast.error("Could not start checkout"); setBusy && setBusy(false); }
}

function Paywall({ icon: Ic, title, desc, price, cta, lookup_key }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="rounded-2xl border border-[#0E9E90]/25 bg-gradient-to-b from-[#12131A] to-[#0A0B10] p-8 md:p-12 text-center qs-aurora" data-testid="paywall">
      <div className="relative max-w-lg mx-auto">
        <span className="grid place-items-center h-14 w-14 mx-auto rounded-2xl border border-[#0E9E90]/40 bg-[#0E9E90]/10 text-[#2DD4BF] mb-5"><Ic size={26} weight="duotone" /></span>
        <h3 className="text-2xl font-black text-white">{title}</h3>
        <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{desc}</p>
        <p className="mt-5 font-mono text-3xl text-white">{price}<span className="text-sm text-[#64748B]">/mo</span></p>
        <button onClick={() => startCheckout(lookup_key, setBusy)} disabled={busy} data-testid="paywall-cta"
          className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#0E9E90] text-white font-medium shadow-[0_0_24px_rgba(14,158,144,0.35)] hover:bg-[#1E4BCC] disabled:opacity-60 transition-colors">
          {busy ? "Starting…" : cta} <ArrowUpRight size={16} />
        </button>
        <p className="mt-4 text-[11px] text-[#64748B] font-mono">Licensed information product. Not financial advice. Markets carry risk.</p>
      </div>
    </div>
  );
}

const DIR_STYLE = { LONG: { c: "#00E676", I: TrendUp }, SHORT: { c: "#FF2E63", I: TrendDown } };

export function SignalsSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const run = () => api.get("/signals").then((r) => setData(r.data)).catch(() => {});
    run(); const t = setInterval(run, 12000); return () => clearInterval(t);
  }, []);
  if (data?.locked) return (
    <Paywall icon={Broadcast} title="Quantum Signals" lookup_key="quantum_signals" price="$99" cta="Get Quantum Signals"
      desc="Real-time trading signal alerts — asset, direction, entry/exit levels and live status — surfaced from Q-Core's real market feeds. A standalone information product; no deposit required." />
  );
  const signals = data?.signals || [];
  return (
    <div className="space-y-4" data-testid="signals-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2"><Broadcast size={20} className="text-[#00E676]" /> Live Signal Feed</h2>
          <p className="text-xs text-[#64748B] mt-1">Auto-updating from real market data · informational technology output, not financial advice.</p>
        </div>
        <span className="qs-pulse-dot inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#00E676]"><span className="h-2 w-2 rounded-full bg-[#00E676]" /> Live</span>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {signals.map((s) => {
          const d = DIR_STYLE[s.direction] || DIR_STYLE.LONG;
          return (
            <div key={s.id} className="qs-beam rounded-xl border border-white/5 bg-[#0A0B10] p-4" data-testid={`signal-${s.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center gap-2">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#0E9E90]">{s.strategy}</span>
                  {s.source === "featured" && <span className="font-mono text-[9px] uppercase rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-amber-300">Featured</span>}
                </span>
                <span className="inline-flex items-center gap-1 font-mono text-xs font-bold" style={{ color: d.c }}><d.I size={14} weight="bold" /> {s.direction}</span>
              </div>
              <p className="text-lg font-bold text-white">{s.asset}</p>
              <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-xs">
                <div className="rounded-lg bg-black/30 border border-white/5 p-2"><p className="text-[9px] uppercase text-[#64748B]">Entry</p><p className="text-white mt-0.5">{s.entry}</p></div>
                <div className="rounded-lg bg-black/30 border border-white/5 p-2"><p className="text-[9px] uppercase text-[#64748B]">Target</p><p className="text-[#00E676] mt-0.5">{s.target}</p></div>
                <div className="rounded-lg bg-black/30 border border-white/5 p-2"><p className="text-[9px] uppercase text-[#64748B]">Stop</p><p className="text-[#FF2E63] mt-0.5">{s.stop || "—"}</p></div>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <div className="flex-1 mr-3">
                  <div className="flex justify-between text-[9px] font-mono text-[#64748B] mb-1"><span>Confidence</span><span>{s.confidence}%</span></div>
                  <div className="h-1 rounded-full bg-white/5 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#0E9E90] to-[#2DD4BF]" style={{ width: `${s.confidence}%` }} /></div>
                </div>
                <span className="font-mono text-[10px] text-[#64748B]">{s.venue}</span>
              </div>
              {s.note && <p className="mt-2 text-xs text-[#94A3B8]">{s.note}</p>}
            </div>
          );
        })}
      </div>
      <p className="text-xs text-[#64748B] font-mono">{data?.note}</p>
    </div>
  );
}

const LEVEL_C = { Beginner: "#00E676", Intermediate: "#2DD4BF", Advanced: "#F59E0B", All: "#94A3B8" };
function LevelChip({ level }) {
  return <span className="font-mono text-[9px] uppercase tracking-widest rounded-full px-2 py-0.5" style={{ background: `${LEVEL_C[level] || "#94A3B8"}1f`, color: LEVEL_C[level] || "#94A3B8" }}>{level}</span>;
}

export function TrainingsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/trainings").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (data?.locked) return (
    <Paywall icon={GraduationCap} title="VIP Trainings & Seminars" lookup_key="vip_membership" price="$499" cta="Upgrade to VIP"
      desc="Unlock live seminars, a full library of past training recordings, and in-depth strategy write-ups. VIP also includes Quantum Signals access." />
  );
  const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";
  return (
    <div className="space-y-8" data-testid="trainings-section">
      <div>
        <h3 className="font-bold text-white mb-3 flex items-center gap-2"><CalendarBlank size={18} className="text-[#2DD4BF]" /> Upcoming Seminars</h3>
        <div className="grid md:grid-cols-2 gap-3">
          {(data?.upcoming || []).map((t) => (
            <div key={t.id} className="qs-beam rounded-xl border border-white/5 bg-[#0A0B10] p-4" data-testid={`training-${t.id}`}>
              <div className="flex items-center justify-between mb-2"><LevelChip level={t.level} /><span className="font-mono text-[10px] text-[#00E676]">LIVE</span></div>
              <p className="font-semibold text-white">{t.title}</p>
              <p className="text-xs text-[#94A3B8] mt-1.5 line-clamp-2">{t.desc}</p>
              <div className="mt-3 flex items-center justify-between font-mono text-[11px] text-[#64748B]"><span>{fmtWhen(t.starts_at)}</span><span>{t.duration_min}m · {t.presenter}</span></div>
            </div>
          ))}
          {!data?.upcoming?.length && <p className="text-sm text-[#64748B] font-mono">No upcoming seminars scheduled.</p>}
        </div>
      </div>
      <div>
        <h3 className="font-bold text-white mb-3 flex items-center gap-2"><PlayCircle size={18} className="text-[#2DD4BF]" /> Training Library</h3>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(data?.recordings || []).map((t) => (
            <a key={t.id} href={t.url || "#"} target="_blank" rel="noopener noreferrer" className="qs-beam group rounded-xl border border-white/5 bg-[#0A0B10] overflow-hidden" data-testid={`training-${t.id}`}>
              <div className="relative aspect-video grid place-items-center" style={{ background: "radial-gradient(120% 120% at 30% 20%, #0E9E9022, #0A0B10 70%)" }}>
                <div className="absolute inset-0 qs-grid opacity-[0.15]" />
                <PlayCircle size={40} weight="fill" className="relative text-white/80 group-hover:scale-110 transition-transform" />
              </div>
              <div className="p-4"><LevelChip level={t.level} /><p className="font-semibold text-white mt-2 leading-snug">{t.title}</p><p className="text-xs text-[#94A3B8] mt-1 line-clamp-2">{t.desc}</p><p className="font-mono text-[10px] text-[#64748B] mt-2">{t.duration_min}m · {t.presenter}</p></div>
            </a>
          ))}
        </div>
      </div>
      <div>
        <h3 className="font-bold text-white mb-3 flex items-center gap-2"><Article size={18} className="text-[#2DD4BF]" /> Strategy & Education</h3>
        <div className="space-y-2">
          {(data?.writeups || []).map((t) => (
            <a key={t.id} href={t.url || "#"} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-3 rounded-xl border border-white/5 bg-[#0A0B10] px-4 py-3 hover:border-white/10 transition-colors" data-testid={`training-${t.id}`}>
              <div className="min-w-0"><p className="font-semibold text-white truncate">{t.title}</p><p className="text-xs text-[#94A3B8] mt-0.5 line-clamp-1">{t.desc}</p></div>
              <ArrowUpRight size={16} className="text-[#2DD4BF] shrink-0" />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}


const TICKET_COLORS = {
  OPEN: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  PENDING: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  RESOLVED: "text-sky-300 border-sky-400/30 bg-sky-400/10",
  CLOSED: "text-slate-400 border-white/10 bg-white/5",
};
const TICKET_CATS = ["General", "Billing & Payments", "Withdrawals", "KYC", "Portfolio / Q-Core", "Technical"];

export function SupportSection() {
  const [tickets, setTickets] = useState([]);
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState("General");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const load = () => api.get("/support/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (active) { const fresh = tickets.find((t) => t.ticket_id === active.ticket_id); if (fresh) setActive(fresh); }
  }, [tickets]); // eslint-disable-line

  const create = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Please add a subject and a message."); return; }
    setBusy(true);
    try {
      await api.post("/support/tickets", { subject, category, message });
      toast.success("Ticket submitted. Our team will respond shortly.");
      setSubject(""); setMessage(""); setCategory("General"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit."); }
    finally { setBusy(false); }
  };
  const sendReply = async () => {
    if (!reply.trim()) return;
    try { await api.post(`/support/tickets/${active.ticket_id}/reply`, { message: reply }); setReply(""); await load(); toast.success("Reply sent."); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed."); }
  };
  const closeTicket = async (tid) => {
    try { await api.post(`/support/tickets/${tid}/close`); await load(); toast.success("Ticket closed."); }
    catch { toast.error("Failed."); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="support-section">
      <div className="space-y-6">
        <div className={card}>
          <h3 className="font-bold text-white mb-1 flex items-center gap-2"><Question size={20} className="text-[#2DD4BF]" weight="duotone" /> Open a Support Ticket</h3>
          <p className="text-xs text-[#64748B] mb-4">Our team typically responds within one business day.</p>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-[#cbd5e1] mb-1.5">Subject</label>
              <input data-testid="ticket-subject" className={inputCls} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of your issue" />
            </div>
            <div>
              <label className="block text-sm text-[#cbd5e1] mb-1.5">Category</label>
              <select data-testid="ticket-category" className={inputCls} value={category} onChange={(e) => setCategory(e.target.value)}>
                {TICKET_CATS.map((c) => <option key={c} value={c} className="bg-[#08090F]">{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm text-[#cbd5e1] mb-1.5">Message</label>
              <textarea data-testid="ticket-message" rows={4} className={inputCls} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Describe your issue in detail…" />
            </div>
            <button onClick={create} disabled={busy} data-testid="ticket-submit" className="w-full px-6 py-3 rounded-full bg-[#2DD4BF] text-[#030305] font-semibold hover:bg-white disabled:opacity-60">{busy ? "Submitting…" : "Submit Ticket"}</button>
          </div>
        </div>
        <div className={card}>
          <h3 className="font-bold text-white mb-3">Your Tickets</h3>
          <div className="space-y-2" data-testid="ticket-list">
            {tickets.length ? tickets.map((t) => (
              <button key={t.ticket_id} onClick={() => setActive(t)} data-testid={`ticket-${t.ticket_id}`}
                className={`w-full flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${active?.ticket_id === t.ticket_id ? "border-[#2DD4BF]/40 bg-[#2DD4BF]/[0.05]" : "border-white/5 bg-black/20 hover:border-white/10"}`}>
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{t.subject}</p>
                  <p className="text-[10px] font-mono text-[#64748B] mt-0.5">{t.ticket_id} · {t.category}</p>
                </div>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase ${TICKET_COLORS[t.status] || ""}`}>{t.status}</span>
              </button>
            )) : <p className="text-sm text-[#64748B] font-mono">No tickets yet.</p>}
          </div>
        </div>
      </div>

      <div className={card}>
        {active ? (
          <div className="flex flex-col h-full" data-testid="ticket-thread">
            <div className="flex items-start justify-between gap-3 pb-4 border-b border-white/10">
              <div className="min-w-0">
                <h3 className="font-bold text-white truncate">{active.subject}</h3>
                <p className="text-[10px] font-mono text-[#64748B] mt-0.5">{active.ticket_id} · {active.category}</p>
              </div>
              <span className={`shrink-0 rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase ${TICKET_COLORS[active.status] || ""}`}>{active.status}</span>
            </div>
            <div className="flex-1 space-y-3 py-4 max-h-[380px] overflow-y-auto">
              {(active.messages || []).map((m, i) => (
                <div key={i} className={`flex ${m.role === "admin" ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${m.role === "admin" ? "bg-[#12131A] border border-white/10 text-[#cbd5e1]" : "bg-[#2DD4BF]/10 border border-[#2DD4BF]/20 text-white"}`}>
                    <p className="text-[10px] font-mono uppercase tracking-wide mb-1 opacity-70">{m.role === "admin" ? "Support Team" : "You"}</p>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                  </div>
                </div>
              ))}
            </div>
            {active.status !== "CLOSED" ? (
              <div className="pt-3 border-t border-white/10 space-y-2">
                <textarea data-testid="ticket-reply-input" rows={2} className={inputCls} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" />
                <div className="flex gap-2">
                  <button onClick={sendReply} data-testid="ticket-reply-send" className="flex-1 px-5 py-2.5 rounded-full bg-[#2DD4BF] text-[#030305] text-sm font-semibold hover:bg-white">Send Reply</button>
                  <button onClick={() => closeTicket(active.ticket_id)} data-testid="ticket-close" className="px-5 py-2.5 rounded-full border border-white/15 text-white text-sm hover:bg-white/5">Close</button>
                </div>
              </div>
            ) : <p className="pt-3 border-t border-white/10 text-xs text-[#64748B] font-mono">This ticket is closed. Open a new ticket if you need further help.</p>}
          </div>
        ) : (
          <div className="h-full grid place-items-center text-sm text-[#64748B] font-mono py-16">Select a ticket to view the conversation.</div>
        )}
      </div>
    </div>
  );
}
